/* eslint-disable react-hooks/exhaustive-deps */
import { DatePicker, Segmented, Spin } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import TreeNodeAnalytics from "./TreeNodeAnalytics.jsx";
import { showErrorNotification } from "../../../utils/notification.jsx";
import { clamp } from "../../../utils/responsiveHelpers";
import AnalyticsGenericBarGraph from "../../common-utils/GenericBarGraph";

const { RangePicker } = DatePicker;

const BLOG_STATISTIC_MODES = {
  GLOBAL: "GLOBAL",
  TREE_NODE: "TREE_NODE"
};

const BlogsStatisticsDashboard = ({ ROOT_URL, BASE_URL }) => {
  const [statisticsMode, setStatisticsMode] = useState(BLOG_STATISTIC_MODES.GLOBAL);
  const [isLoading, setIsLoading] = useState(false);
  const [isTreeLoading, setIsTreeLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [selectedNodeIdentifier, setSelectedNodeIdentifier] = useState(null);
  const [filter, setFilter] = useState({
    startDate: dayjs().subtract(1, "month").startOf("day").toDate(),
    endDate: dayjs().endOf("day").toDate()
  });

  // Global stats related states
  const [dailyVisitGraphPoints, setDailyVisitGraphPoints] = useState([]);

  // Tree node level stats related states
  const [treeNodeStatsData, setTreeNodeStatsData] = useState({});

  const fetchGlobalData = async () => {
    setIsLoading(true);
    try {
      const dailyVisitGraphPointsResponse = await axios.get(
        `${ROOT_URL}/${BASE_URL}/daily-visit-graph-points`,
        {
          params: { filter }
        }
      );
      setDailyVisitGraphPoints(dailyVisitGraphPointsResponse?.data || []);
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTreeNodeStatsData = async () => {
    if (!selectedNodeIdentifier) {
      return;
    }
    setIsLoading(true);
    try {
      const treeNodeStatsData = await axios.get(`${ROOT_URL}/${BASE_URL}/tree-node`, {
        params: { activeUniqueUrlIdentifier: selectedNodeIdentifier, filter }
      });
      setTreeNodeStatsData(treeNodeStatsData?.data || {});
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setIsLoading(false);
    }
  };

  const buildTreeData = (nodes = []) => {
    return nodes.map((node) => ({
      title: node.title,
      key: node.activeUniqueUrlIdentifier,
      children: buildTreeData(node.children || [])
    }));
  };

  const loadTree = async () => {
    setIsTreeLoading(true);
    try {
      const { data } = await axios.get(`${ROOT_URL}/admin/cp-digest/management/tree-node/fetch`);
      setTreeData(buildTreeData(data || []));
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setIsTreeLoading(false);
    }
  };

  useEffect(() => {
    if (statisticsMode === BLOG_STATISTIC_MODES.GLOBAL) {
      fetchGlobalData();
    } else {
      fetchTreeNodeStatsData();
    }
  }, [statisticsMode, filter, selectedNodeIdentifier]);

  useEffect(() => {
    loadTree();
  }, []);

  return (
    <div style={{ padding: `20px ${clamp(12, 40)}` }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          marginBottom: "20px"
        }}
      >
        <h2>Blog Analytics Dashboard</h2>
        <Segmented
          options={[
            { label: "Global Analytics", value: BLOG_STATISTIC_MODES.GLOBAL },
            { label: "Blog-Level Analytics", value: BLOG_STATISTIC_MODES.TREE_NODE }
          ]}
          value={statisticsMode}
          onChange={setStatisticsMode}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "10px",
          marginBottom: "20px"
        }}
      >
        <span>Date filter:</span>
        <RangePicker
          style={{ width: "min(260px, 100%)" }}
          value={
            filter.startDate && filter.endDate
              ? [dayjs(filter.startDate), dayjs(filter.endDate)]
              : []
          }
          onChange={(dates) => {
            if (dates) {
              setFilter({
                startDate: dates[0].clone().startOf("day").toDate(),
                endDate: dates[1].clone().endOf("day").toDate()
              });
            } else {
              setFilter({
                startDate: null,
                endDate: null
              });
            }
          }}
        />
      </div>

      {statisticsMode === BLOG_STATISTIC_MODES.GLOBAL && (
        <>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "60vh",
                width: "100%"
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <>
              <h3>Daily Visit Logged-in Users Count</h3>
              <p style={{ marginBottom: "10px" }}>
                This graph shows the total visits on CP Digest aggregated by date. This count
                includes all page loads and refreshes from logged-in users.
              </p>
              <AnalyticsGenericBarGraph
                dataPoints={dailyVisitGraphPoints?.loggedInTotalVisitsGraphPoints}
              />

              <h3>Unique Daily Logged-in Users Visit Count</h3>
              <p style={{ marginBottom: "10px" }}>
                This graph shows unique user visits per day. Even if a user visits the page multiple
                times, it counts as one per day.
              </p>
              <AnalyticsGenericBarGraph
                dataPoints={dailyVisitGraphPoints?.loggedInUniqueVisitsGraphPoints}
              />

              <h3>Daily Visit Non-logged-in Users Count</h3>
              <p style={{ marginBottom: "10px" }}>
                This graph shows total visits by non-logged-in users, aggregated by date. Includes
                all page loads and refreshes.
              </p>
              <AnalyticsGenericBarGraph
                dataPoints={dailyVisitGraphPoints?.nonLoggedInTotalVisitsGraphPoints}
              />
            </>
          )}
        </>
      )}

      {statisticsMode === BLOG_STATISTIC_MODES.TREE_NODE && (
        <TreeNodeAnalytics
          treeData={treeData}
          isTreeLoading={isTreeLoading}
          selectedNodeIdentifier={selectedNodeIdentifier}
          setSelectedNodeIdentifier={setSelectedNodeIdentifier}
          isLoading={isLoading}
          treeNodeStatsData={treeNodeStatsData}
        />
      )}
    </div>
  );
};

export default BlogsStatisticsDashboard;
